import { Timer } from "@phosphor-icons/react";
import { EmptyState } from "../shared/EmptyState";

const KIND_LABEL = { stat: "Statistic", adj: "Adjustment", result: "Result" };
const KIND_COLOR = { stat: "text-sky-400", adj: "text-amber-400", result: "text-emerald-400" };

const ComponentRow = ({ label, kind, value, testId }) => (
  <div data-testid={testId} className="flex items-center justify-between gap-3 border-b border-white/5 py-1.5 last:border-0">
    <span className="font-mono text-[11px] text-zinc-500">
      {label}
      {kind && <span className={`ml-1.5 font-mono text-[9px] uppercase tracking-wide ${KIND_COLOR[kind]}`}>{KIND_LABEL[kind]}</span>}
    </span>
    <span className="font-mono text-xs text-zinc-200">{value}</span>
  </div>
);

// Same delta-vs-statistic convention as the V2 card: adjustments show a clean signed value
// (contribution to expected runs, i.e. the negation of the engine's own stored adjustment,
// since the engine SUBTRACTS it), never presented like a raw statistic.
const starterValue = (adj) => (adj == null ? "Unavailable" : `${adj > 0 ? "-" : adj < 0 ? "+" : ""}${Math.abs(adj).toFixed(2)} runs`);

const auditValue = (audit, key, suffix = "") => (audit?.[key] != null ? `${audit[key]}${suffix}` : "Unavailable");

export const F5RunEnvironmentCard = ({ f5Total, awayLabel, homeLabel, testId }) => {
  if (!f5Total) {
    return (
      <div data-testid={testId} className="flex flex-col gap-3">
        <EmptyState icon={Timer} title="First 5 Innings Model Unavailable" description="Loading analytical model data." testId={`${testId}-empty`} />
      </div>
    );
  }

  const ok = f5Total.status === "ok";
  const c = f5Total.components || {};
  const sp = c.starting_pitcher || {};

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">First 5 Innings Run Environment</p>
        <span data-testid={`${testId}-badge`} className="rounded-full border border-eb-accent/30 bg-eb-accent/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide text-eb-accent">
          Analytical / Experimental
        </span>
      </div>

      {ok ? (
        <>
          <div className="rounded-lg border border-white/10 bg-eb-bg p-4 text-center">
            <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">F5 Projected Total Runs</span>
            <p data-testid={`${testId}-projected-total`} className="mt-1 font-heading text-3xl text-white">
              {f5Total.projected_f5_total.toFixed(2)}
            </p>
            <p data-testid={`${testId}-breakdown`} className="mt-1 font-mono text-[11px] text-zinc-500">
              {f5Total.expected_home_f5_runs.toFixed(2)} {homeLabel || "Home"} + {f5Total.expected_away_f5_runs.toFixed(2)} {awayLabel || "Away"}
            </p>
            {f5Total.threshold_status !== "not_applicable" && f5Total.f5_total_threshold != null && (
              <div className="mt-3 flex items-center justify-center gap-4 font-mono text-xs">
                <span data-testid={`${testId}-over-prob`} className="text-emerald-400">
                  Over {f5Total.f5_total_threshold}: {(f5Total.over_probability * 100).toFixed(1)}%
                </span>
                <span data-testid={`${testId}-under-prob`} className="text-rose-400">
                  Under {f5Total.f5_total_threshold}: {(f5Total.under_probability * 100).toFixed(1)}%
                </span>
                {f5Total.push_probability > 0 && <span className="text-zinc-500">Push: {(f5Total.push_probability * 100).toFixed(1)}%</span>}
              </div>
            )}
          </div>

          <div data-testid={`${testId}-market`} className="rounded-lg border border-white/10 bg-eb-bg p-3">
            <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">F5 Market Total</span>
            {f5Total.market_line ? (
              <p className="mt-1 font-mono text-sm text-zinc-200">
                {f5Total.market_line.line_value} <span className="text-zinc-500">({f5Total.market_line.sportsbook})</span>
              </p>
            ) : (
              <p className="mt-1 font-mono text-sm text-zinc-500">No F5 market line synced yet</p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div data-testid={`${testId}-components-home`} className="rounded-lg border border-white/10 bg-eb-bg p-3">
              <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">{homeLabel || "Home"} Components</span>
              <ComponentRow label="Baseline F5 Expected Runs" kind="result" value={c.baseline?.expected_home_f5_runs?.toFixed(2)} testId={`${testId}-home-baseline`} />
              <ComponentRow label="Starting Pitcher Adjustment" kind="adj" value={starterValue(sp.home_run_adjustment)} testId={`${testId}-home-starter-adj`} />
              <ComponentRow label="Starter ERA" kind="stat" value={auditValue(sp.home_audit, "era")} testId={`${testId}-home-starter-era`} />
              <ComponentRow label="Starter WHIP / K-BB" kind="stat" value={sp.home_audit?.whip != null ? `${sp.home_audit.whip} / ${sp.home_audit.strikeout_walk_ratio ?? "--"}` : "Unavailable"} testId={`${testId}-home-starter-audit`} />
              <ComponentRow label="Starter Avg IP/Start" kind="stat" value={auditValue(sp.home_audit, "avg_innings_per_start")} testId={`${testId}-home-starter-ip`} />
              <ComponentRow label="Starter Throws" kind="stat" value={sp.home_throws || "Unavailable"} testId={`${testId}-home-starter-throws`} />
              <ComponentRow label="Final F5 Expected Runs" kind="result" value={f5Total.expected_home_f5_runs.toFixed(2)} testId={`${testId}-home-final`} />
            </div>
            <div data-testid={`${testId}-components-away`} className="rounded-lg border border-white/10 bg-eb-bg p-3">
              <span className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">{awayLabel || "Away"} Components</span>
              <ComponentRow label="Baseline F5 Expected Runs" kind="result" value={c.baseline?.expected_away_f5_runs?.toFixed(2)} testId={`${testId}-away-baseline`} />
              <ComponentRow label="Starting Pitcher Adjustment" kind="adj" value={starterValue(sp.away_run_adjustment)} testId={`${testId}-away-starter-adj`} />
              <ComponentRow label="Starter ERA" kind="stat" value={auditValue(sp.away_audit, "era")} testId={`${testId}-away-starter-era`} />
              <ComponentRow label="Starter WHIP / K-BB" kind="stat" value={sp.away_audit?.whip != null ? `${sp.away_audit.whip} / ${sp.away_audit.strikeout_walk_ratio ?? "--"}` : "Unavailable"} testId={`${testId}-away-starter-audit`} />
              <ComponentRow label="Starter Avg IP/Start" kind="stat" value={auditValue(sp.away_audit, "avg_innings_per_start")} testId={`${testId}-away-starter-ip`} />
              <ComponentRow label="Starter Throws" kind="stat" value={sp.away_throws || "Unavailable"} testId={`${testId}-away-starter-throws`} />
              <ComponentRow label="Final F5 Expected Runs" kind="result" value={f5Total.expected_away_f5_runs.toFixed(2)} testId={`${testId}-away-final`} />
            </div>
          </div>

          <ComponentRow label="Park Factor (100 = neutral)" value={c.park?.applied ? c.park.run_factor.toFixed(1) : "Not Applied"} testId={`${testId}-park-adj`} />
          <p data-testid={`${testId}-bullpen-note`} className="font-mono text-[10px] text-zinc-500">
            Bullpen workload/quality is never used in this model — {c.bullpen?.reason}.
          </p>

          <p data-testid={`${testId}-legend`} className="font-mono text-[10px] text-zinc-500">
            <span className={KIND_COLOR.stat}>Statistic</span> = a real per-game/per-starter fact. <span className={KIND_COLOR.adj}>Adjustment</span> = a
            signed delta contributed to expected runs. <span className={KIND_COLOR.result}>Result</span> = an expected-run value at that step.
          </p>

          <p data-testid={`${testId}-meta`} className="font-mono text-[10px] text-zinc-500">
            Model: {f5Total.model_version} &mdash; derived from each team's own real first-5-innings run history (not
            full_game &times; 5/9), with a starter-weighted adjustment and park factor. Not a trained/calibrated model, not
            compared against the full-game V1/V2 totals, and contains no market-comparison or profitability information.
            Feature version: {f5Total.feature_version}.
          </p>
        </>
      ) : (
        <EmptyState icon={Timer} title="Insufficient Data For F5 Projection" description={f5Total.reason || "Not enough first-5-innings data yet."} testId={`${testId}-insufficient`} />
      )}
    </div>
  );
};
